/**
 * API Keys Service
 * Service for managing API keys (access tokens)
 */

import { api } from './api'

export interface ApiKey {
  id: number
  key_prefix: string
  label: string | null
  permissions: string[]
  last_used_at: number | null
  expires_at: number | null
  status: 'active' | 'revoked' | 'expired'
  created_at: number
  updated_at: number
}

export interface ApiKeyWithKey extends ApiKey {
  key: string // Full key only returned on creation
}

export interface CreateApiKeyRequest {
  label?: string
  permissions?: string[]
  expires_at?: number
}

export interface UpdateApiKeyRequest {
  label?: string | null
  permissions?: string[]
  status?: 'active' | 'revoked' | 'expired'
}

export interface ListApiKeysResponse {
  api_keys: ApiKey[]
}

export interface CreateApiKeyResponse {
  api_key: ApiKeyWithKey
  message: string
}

export interface ApiKeyResponse {
  api_key: ApiKey
}

export interface DeleteApiKeyResponse {
  success: boolean
  message: string
}

/**
 * API Keys Service
 */
export const apiKeysService = {
  /**
   * List all API keys for current user
   */
  async list(): Promise<ApiKey[]> {
    const response = await api.get<ListApiKeysResponse>('/api-keys')
    return response.api_keys
  },

  /**
   * Get specific API key by ID
   */
  async get(id: number): Promise<ApiKey> {
    const response = await api.get<ApiKeyResponse>(`/api-keys/${id}`)
    return response.api_key
  },

  /**
   * Create new API key
   */
  async create(data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    return api.post<CreateApiKeyResponse>('/api-keys', data)
  },

  /**
   * Update API key
   */
  async update(id: number, data: UpdateApiKeyRequest): Promise<void> {
    await api.patch(`/api-keys/${id}`, data)
  },

  /**
   * Delete (revoke) API key
   */
  async delete(id: number): Promise<void> {
    await api.delete<DeleteApiKeyResponse>(`/api-keys/${id}`)
  },
}
